import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Tooltip } from '@mui/material';
import { getFullName } from '@src/utilities/nameHelper';
import classNames from 'classnames';
import { isEmpty, uniq } from 'lodash';
import { useLocale } from 'pe-core-frontend';
import { useMemo } from 'react';
export const AuditLogRow = ({ auditLog, entitiesByTypeAndId, employeeFieldsByCode, }) => {
    const { t, formatToLocalDatetime } = useLocale();
    const changedFields = useMemo(() => {
        let fields = uniq([
            ...Object.keys(auditLog.originalValues || {}),
            ...Object.keys(auditLog.newValues || {}),
        ]);
        fields = fields.filter(field => {
            if (['created_at', 'updated_at'].includes(field)) {
                // remove created_at and updated_at
                return false;
            }
            // remove the fields that are empty in both original and new values
            if ((!auditLog.originalValues || !auditLog.originalValues[field]) &&
                (!auditLog.newValues || !auditLog.newValues[field])) {
                return false;
            }
            if (field === 'documents') {
                // check if docs are empty
                if ((!auditLog.originalValues ||
                    !auditLog.originalValues.documents ||
                    !auditLog.originalValues.documents.docs.length) &&
                    (!auditLog.newValues ||
                        !auditLog.newValues.documents ||
                        !auditLog.newValues.documents.docs.length)) {
                    return false;
                }
            }
            // if both original or new values are objects and are empty
            if ((typeof auditLog.originalValues[field] === 'object' ||
                typeof auditLog.newValues[field] === 'object') &&
                isEmpty(auditLog.originalValues[field]) &&
                isEmpty(auditLog.newValues[field])) {
                return false;
            }
            return true;
        });
        return fields;
    }, [auditLog.newValues, auditLog.originalValues]);
    const numFields = changedFields.length;
    const renderField = (changeSet, field) => {
        if (!changeSet || changeSet[field] === undefined || changeSet[field] === null) {
            return '-';
        }
        const value = changeSet[field];
        if (typeof value === 'string') {
            if (field.toLowerCase().endsWith('_date')) {
                return formatToLocalDatetime(value, { dateStyle: 'medium' });
            }
            if (field.toLowerCase().endsWith('_at')) {
                return formatToLocalDatetime(value, { dateStyle: 'medium', timeStyle: 'medium' }, undefined, true);
            }
            return value;
        }
        if (typeof value === 'boolean') {
            return value ? 'true' : 'false';
        }
        if (field === 'documents') {
            return renderDocuments(value);
        }
        if (typeof value === 'object') {
            return (_jsx("pre", { dangerouslySetInnerHTML: {
                    __html: JSON.stringify(value, null, 2).replace(/\n/g, '<br/>'),
                } }));
        }
        return value;
    };
    const renderDocuments = (docs) => {
        return (_jsx("ul", { className: 'list-disc pl-4', children: docs.docs.map(doc => (_jsx("li", { children: doc.name }, doc.s3Key))) }));
    };
    const isFieldDifferentFromCurrent = (field) => {
        return (auditLog.newValues &&
            JSON.stringify(auditLog.newValues[field]) !==
                JSON.stringify(entitiesByTypeAndId[auditLog.tableName][auditLog.recordId][field]));
    };
    return changedFields.map((field, idx) => (_jsxs("tr", { className: classNames(idx === numFields - 1 && 'border-b-gray-600'), children: [idx === 0 && (_jsxs(_Fragment, { children: [_jsx("td", { className: 'border border-gray-300 border-l-gray-600 border-b-gray-600', rowSpan: numFields, children: _jsx(Tooltip, { title: _jsxs("div", { children: [t('Audit log id'), " - ", auditLog.id] }), children: _jsx("span", { children: formatToLocalDatetime(auditLog.updatedAt, {
                                    dateStyle: 'medium',
                                    timeStyle: 'medium',
                                }, undefined, true) }) }) }), _jsx("td", { className: 'border border-gray-300 border-b-gray-600', rowSpan: numFields, children: t(`Entity.${auditLog.tableName}`) })] })), _jsxs("td", { className: classNames('border border-gray-300', idx === numFields - 1 && 'border-b-gray-600'), children: [field, auditLog.tableName === 'employees' && employeeFieldsByCode[field] && (_jsxs("div", { className: 'text-sm text-gray-900', children: ["(", employeeFieldsByCode[field].name, ")"] }))] }), _jsx("td", { className: classNames('border border-gray-300', idx === numFields - 1 && 'border-b-gray-600'), children: renderField(auditLog.originalValues, field) }), _jsx("td", { className: classNames('border border-gray-300', idx === numFields - 1 && 'border-b-gray-600', !isFieldDifferentFromCurrent(field) && 'text-blue'), children: _jsx(Tooltip, { title: !isFieldDifferentFromCurrent(field) ? (_jsx("div", { className: 'flex flex-col', children: _jsx("span", { children: t('Same as current Value') }) })) : undefined, children: _jsx("div", { className: 'overflow-x-auto max-w-[400px]', children: renderField(auditLog.newValues, field) }) }) }), idx === 0 && (_jsxs(_Fragment, { children: [_jsx("td", { className: 'border border-l-gray-300 border-b-gray-600', rowSpan: numFields, children: _jsx(Tooltip, { title: _jsxs("div", { className: 'flex flex-col gap-3', children: [_jsxs("div", { className: 'flex flex-col', children: [_jsx("span", { children: t('Session ID') }), _jsx("span", { children: auditLog.sessionId })] }), _jsxs("div", { className: 'flex flex-col', children: [_jsx("span", { children: t('Request ID') }), _jsx("span", { children: auditLog.requestId })] })] }), children: _jsx("span", { children: auditLog.updatedBy && getFullName(auditLog.updatedBy) }) }) }), _jsx("td", { className: 'border border-l-gray-300 border-r-gray-600 border-b-gray-600', rowSpan: numFields, children: auditLog.operation })] }))] }, `${auditLog.id}-${field}`)));
};
